class ProductUpsellsSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector(".splide");
    this.initialized = false;

    // Listen for Shopify theme editor events
    document.addEventListener("shopify:section:load", (event) => {
      if (this.isRelevantSection(event)) {
        this.initSlider();
      }
    });

    document.addEventListener("shopify:block:select", (event) => {
      if (this.isRelevantBlock(event)) {
        this.initSlider();
      }
    });
  }

  connectedCallback() {
    // Wait for Splide to be loaded before initializing
    if (typeof Splide !== 'undefined') {
      this.initSlider();
    } else {
      // Poll for Splide to be available
      const checkSplide = setInterval(() => {
        if (typeof Splide !== 'undefined') {
          clearInterval(checkSplide);
          this.initSlider();
        }
      }, 50);
      
      // Timeout after 5 seconds to prevent infinite polling
      setTimeout(() => clearInterval(checkSplide), 5000);
    }
  }

  isRelevantSection(event) {
    return this.dataset.sectionId === event.detail.sectionId;
  }

  isRelevantBlock(event) {
    return (
      this.dataset.sectionId === event.detail.sectionId &&
      this.dataset.blockId === event.detail.blockId
    );
  }

  initSlider() {
    // If Splide is not loaded or slider element doesn't exist, don't proceed
    if (typeof Splide === "undefined" || !this.slider) {
      return;
    }
    
    // Remove initialized class during re-initialization
    this.classList.remove('is-initialized');

    // If already initialized, destroy the previous instance
    if (this.splideInstance) {
      this.splideInstance.destroy();
      this.initialized = false;
    }

    // Apply arrow customization attributes to the slider element
    const arrowPosition = this.dataset.arrowPosition || "edges";
    const arrowSize = this.dataset.arrowSize || "medium";
    const arrowIcon = this.dataset.arrowIcon || "chevron_left_right";
    const arrowFilled = this.dataset.arrowFilled || "false";
    const arrowColor = this.dataset.arrowColor || "#000000";

    this.setAttribute("data-arrow-position", arrowPosition);
    this.setAttribute("data-arrow-size", arrowSize);
    this.setAttribute("data-arrow-icon", arrowIcon);
    this.setAttribute("data-arrow-filled", arrowFilled);
    this.setAttribute("data-arrow-color", arrowColor);

    // Get configuration from data attributes
    const config = this.getConfig();

    // Initialize Splide
    this.splideInstance = new Splide(this.slider, config);

    // Set up event handlers
    this.setupEventHandlers();

    // Mount the slider
    this.splideInstance.mount();

    // Set up event handlers
    this.setupEventHandlers();

    this.initialized = true;
  }

  getConfig() {
    const perPageDesktop = this.dataset.slidesDesktop;
    const perPageMobile = this.dataset.slidesMobile;

    // Default configuration
    const config = {
      type: "slide",
      perPage: perPageDesktop === "1.5" ? 1 : parseInt(perPageDesktop, 10),
      padding: { right: perPageDesktop === "1.5" ? "40%" : 0 },
      gap: parseInt(this.dataset.gap, 10),
      arrows: this.dataset.arrows !== "false",
      pagination: this.dataset.pagination !== "false",
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      focus: 0, // Focus on the first slide to enable proper navigation
      trimSpace: false, // Don't trim spaces to allow navigation to last slide
      breakpoints: {
        749: {
          perPage: perPageMobile === "1.5" ? 1 : parseInt(perPageMobile, 10),
          padding: { right: perPageMobile === "1.5" ? "30%" : 0 }
        },
      },
    };

    // Add optional configurations if they exist
    if (this.dataset.autoplay === "true") {
      config.autoplay = true;
      config.interval = parseInt(this.dataset.autoplaySpeed, 10) * 1000;
    }

    if (this.dataset.rewind === "true") {
      config.rewind = true;
    }

    if (this.dataset.focus) {
      config.focus = this.dataset.focus;
    }

    if (this.dataset.drag === "false") {
      config.drag = false;
    }

    return config;
  }

  setupEventHandlers() {
    // Handle slide change events
    this.splideInstance.on("moved", () => {
      this.handleSlideChange();
    });

    this.splideInstance.on("mounted", () => {
      this.handleSlideChange();

      // Equalize heights after a short delay to ensure all content is rendered
      setTimeout(() => {
        this.equalizeSlideHeights();
        // Mark as initialized to show slider (controlled by CSS)
        this.classList.add('is-initialized');
      }, 100);

      // Add resize handler for responsive behavior
      window.addEventListener(
        "resize",
        this.debounce(() => {
          this.handleSlideChange();
          this.equalizeSlideHeights();
        }, 200)
      );
    });
  }

  equalizeSlideHeights() {
    // Reset heights first
    const slides = this.splideInstance.Components.Elements.slides;
    slides.forEach((slide) => {
      const item = slide.querySelector(".product-upsell-item");
      if (item) {
        item.style.height = "";
        const inner = item.querySelector(".product-upsell-item__inner");
        if (inner) inner.style.height = "";
        const content = item.querySelector(".product-upsell-item__content");
        if (content) content.style.height = "";
      }
    });

    // Get the tallest height
    let maxHeight = 0;
    slides.forEach((slide) => {
      const item = slide.querySelector(".product-upsell-item");
      if (item) {
        const height = item.offsetHeight;
        maxHeight = Math.max(maxHeight, height);
      }
    });

    // Apply the height to all slides
    if (maxHeight > 0) {
      slides.forEach((slide) => {
        const item = slide.querySelector(".product-upsell-item");
        if (item) {
          item.style.height = `${maxHeight}px`;
          item.classList.add("js-equal-height");
        }
      });
    }
  }

  handleSlideChange() {
    const activeSlide =
      this.splideInstance.Components.Elements.slides[this.splideInstance.index];
    if (activeSlide) {
      // Remove active class from all slides
      this.splideInstance.Components.Elements.slides.forEach((slide) => {
        slide.classList.remove("is-active-slide");
      });

      // Add active class to current slide
      activeSlide.classList.add("is-active-slide");

      // Hide pagination and arrows if all slides fit in the view
      this.updateNavigationVisibility();
    }
  }

  updateNavigationVisibility() {
    // Check if all slides fit in the view without scrolling
    const Components = this.splideInstance.Components;
    const totalSlides = Components.Elements.slides.length;
    const perPage = this.splideInstance.options.perPage;
    const shouldHideNavigation = totalSlides <= perPage;

    // Get pagination and arrow elements
    const pagination = Components.Elements.root.querySelector(
      ".splide__pagination"
    );
    const arrows = Components.Elements.root.querySelectorAll(".splide__arrow");

    // Hide/show pagination based on whether all slides fit
    if (pagination) {
      pagination.classList.toggle("hidden", shouldHideNavigation);
    }

    // Hide/show arrows based on whether all slides fit
    if (arrows?.length) {
      arrows.forEach((arrow) => {
        arrow.classList.toggle("hidden", shouldHideNavigation);
      });
    }
  }

  // Utility function for debouncing resize events
  debounce(func, wait) {
    let timeout;
    return () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this), wait);
    };
  }
}

// Register the custom element
customElements.define("product-upsells-slider", ProductUpsellsSlider);
